import React, { useState, useCallback, useEffect } from "react";
import { Row, Col, Button } from "react-bootstrap";
import Select from "react-select";
import {
  format,
  parse,
  startOfYear,
  endOfYear,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import RsDateRangePicker from "../utils/RsDateRangePicker";

const DATE_FMT = "yyyy-MM-dd";

const MODE_OPTIONS = [
  { value: "yoy", label: "Year on Year" },
  { value: "mom", label: "Month on Month" },
  { value: "custom", label: "Custom Range" },
];

const MONTHS = [
  { value: 0, label: "January" },
  { value: 1, label: "February" },
  { value: 2, label: "March" },
  { value: 3, label: "April" },
  { value: 4, label: "May" },
  { value: 5, label: "June" },
  { value: 6, label: "July" },
  { value: 7, label: "August" },
  { value: 8, label: "September" },
  { value: 9, label: "October" },
  { value: 10, label: "November" },
  { value: 11, label: "December" },
];

function getYearOptions() {
  const currentYear = new Date().getFullYear();
  const years = [];
  // go 5 years into future to always accommodate new years
  for (let y = currentYear + 5; y >= 2000; y--) {
    years.push({ value: y, label: String(y) });
  }
  return years;
}

const YEAR_OPTIONS = getYearOptions();

function parseQueryString(queryString, mode) {
  if (!queryString) return [];

  // Handle both "dateRanges=..." and raw encoded/plain value
  let raw = queryString;
  if (raw.includes("dateRanges=")) {
    const match = raw.match(/dateRanges=([^&]*)/);
    if (!match) return [];
    raw = match[1];
  }

  const decoded = decodeURIComponent(raw);
  if (!decoded) return [];

  return decoded.split(";").filter(Boolean).map((seg) => {
    const [startDate, endDate, label, yearVal] = seg.split("|");
    const year = yearVal ? Number(yearVal) : new Date().getFullYear();

    if (mode === "mom") {
      const monthObj = MONTHS.find((m) => m.label === label);
      const month = monthObj ? monthObj.value : new Date().getMonth();
      return { id: nextId(), startDate, endDate, year, month };
    }

    return { id: nextId(), startDate, endDate, year };
  });
}
// Build the pipe-delimited segment for one period
function buildPeriodString(period, mode) {
  const { startDate, endDate, year, month } = period;
  let label = "";
  let yearVal = "";

  if (mode === "yoy") {
    label = "Year";
    yearVal = String(year);
  } else if (mode === "mom") {
    const monthObj = MONTHS.find((m) => m.value === month);
    label = monthObj ? monthObj.label : "Month";
    yearVal = String(year);
  } else {
    label = "Custom";
    yearVal = startDate ? startDate.slice(0, 4) : "";
  }

  return `${startDate}|${endDate}|${label}|${yearVal}`;
}

// Build the full query string: dateRanges=seg1;seg2;...
function buildQueryString(periods, mode) {
  const parts = periods.map((p) => buildPeriodString(p, mode));
  return `${encodeURIComponent(parts.join(";"))}`;
}

let _pid = 0;
function nextId() {
  return ++_pid;
}

function makePeriod(mode, index = 0) {
  const now = new Date();
  const currentYear = now.getFullYear();

  if (mode === "yoy") {
    const year = currentYear - index;
    return {
      id: nextId(),
      year,
      startDate: format(startOfYear(new Date(year, 0)), DATE_FMT),
      endDate: format(endOfYear(new Date(year, 0)), DATE_FMT),
    };
  }

  if (mode === "mom") {
    const d = new Date(now.getFullYear(), now.getMonth() - index, 1);
    return {
      id: nextId(),
      year: d.getFullYear(),
      month: d.getMonth(),
      startDate: format(startOfMonth(d), DATE_FMT),
      endDate: format(endOfMonth(d), DATE_FMT),
    };
  }

  // custom
  return {
    id: nextId(),
    startDate: format(startOfMonth(now), DATE_FMT),
    endDate: format(endOfMonth(now), DATE_FMT),
  };
}


/**
 * PeriodFilter
 *
 * Props:
 *   onChange(queryString: string, periods: array) — fires on every change
 *   defaultMode?: 'yoy' | 'mom' | 'custom'
 */
export default function PeriodFilter({ onChange, defaultMode = "yoy", queryString = "" , handleMode}) {
  const [mode, setMode] = useState(defaultMode);
  const [periods, setPeriods] = useState(() => parseQueryString(queryString, defaultMode));


  // ── helpers ────────────────────────────────────────────────────────────────

  const notify = useCallback(
    (nextPeriods, nextMode) => {
      const resolvedMode = nextMode ?? mode;
      onChange?.(buildQueryString(nextPeriods, resolvedMode), nextPeriods);
    },
    [mode, onChange]
  );




   useEffect(() => {
  
   const next = [makePeriod(mode, 0)];
    
    setMode(mode);
    setPeriods(next);
    notify(next, mode);
   
      // setPeriods([makePeriod(mode, 0)]);


  }, [mode]);
  
  function addPeriod() {
    const next = [...periods, makePeriod(mode, periods.length)];
    setPeriods(next);
    notify(next);
  }

  function removePeriod(id) {
  if (periods.length === 1) {
    // Clear the single period instead of removing it
    const cleared = periods.map((p) => {
      if (p.id !== id) return p;
      if (mode === "yoy") return { ...p, startDate: "", endDate: "" };
      if (mode === "mom") return { ...p, startDate: "", endDate: "" };
      return { ...p, startDate: "", endDate: "" };
    });
    setPeriods([]);
    notify([]);
    return;
  }
  const next = periods.filter((p) => p.id !== id);
  setPeriods(next);
  notify(next);
}

  function updatePeriod(id, patch) {
    const next = periods.map((p) => {
      if (p.id !== id) return p;
      const updated = { ...p, ...patch };

      // Auto-recalculate full year range when year changes in YoY mode
      if (mode === "yoy" && patch.year !== undefined) {
        updated.startDate = format(startOfYear(new Date(updated.year, 0)), DATE_FMT);
        updated.endDate = format(endOfYear(new Date(updated.year, 0)), DATE_FMT);
      }

      // Auto-recalculate month range when year or month changes in MoM mode
      if (mode === "mom" && (patch.year !== undefined || patch.month !== undefined)) {
        const d = new Date(updated.year, updated.month, 1);
        updated.startDate = format(startOfMonth(d), DATE_FMT);
        updated.endDate = format(endOfMonth(d), DATE_FMT);
      }

      return updated;
    });
    setPeriods(next);
    notify(next);
  }

  // ── date range picker handlers ─────────────────────────────────────────────

  function handleDateRangeOk(dates, id) {
    if (!dates || dates.length < 2) return;
    updatePeriod(id, {
      startDate: format(dates[0], DATE_FMT),
      endDate: format(dates[1], DATE_FMT),
    });
  }

  function clearDateRange(id, period) {
    // Reset to the full range implied by the period's current year/month
    // if (mode === "yoy") {
    //   updatePeriod(id, {
    //     startDate: format(startOfYear(new Date(period.year, 0)), DATE_FMT),
    //     endDate: format(endOfYear(new Date(period.year, 0)), DATE_FMT),
    //   });
    // } else if (mode === "mom") {
    //   const d = new Date(period.year, period.month, 1);
    //   updatePeriod(id, {
    //     startDate: format(startOfMonth(d), DATE_FMT),
    //     endDate: format(endOfMonth(d), DATE_FMT),
    //   });
    // } else {
    //   const now = new Date();
    //   updatePeriod(id, {
    //     startDate: format(startOfMonth(now), DATE_FMT),
    //     endDate: format(endOfMonth(now), DATE_FMT),
    //   });
    // }

      updatePeriod(id, { startDate: "", endDate: "" });

  }

  // ── date range picker value/bounds helpers ─────────────────────────────────

  function pickerValue(period) {
    if (period.startDate && period.endDate) {
      return [
        parse(period.startDate, DATE_FMT, new Date()),
        parse(period.endDate, DATE_FMT, new Date()),
      ];
    }
    return [];
  }

  // For YoY/MoM: constrain picker to the selected year/month boundaries
  function pickerBounds(period) {
    if (mode === "yoy") {
      return {
        disabledDate: (date) => {
          const yr = period.year;
          return (
            date < new Date(yr, 0, 1) || date > new Date(yr, 11, 31)
          );
        },
      };
    }
    if (mode === "mom") {
      const start = startOfMonth(new Date(period.year, period.month, 1));
      const end = endOfMonth(new Date(period.year, period.month, 1));
      return {
        disabledDate: (date) => date < start || date > end,
      };
    }
    return {};
  }

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div className=" mb-3">
      {/* Global mode selector */}
      <Row className="g-2 mb-3 align-items-end">
        <Col xs={12} sm={4} md={3}>
          <small className="text-muted d-block mb-1">Comparison type</small>
          {/* <Select
                          classNamePrefix="form-select"
            options={MODE_OPTIONS}
            value={MODE_OPTIONS.find((m) => m.value === mode) ?? null}
            onChange={(opt) => switchMode(opt.value)}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            isSearchable={false}
          /> */}
        </Col>
      </Row>

      {/* Period rows */}
      <div className="d-flex flex-column gap-2">
        {periods.map((period, idx) => (
          <div
            key={period.id}
            className="border rounded p-2 bg-white"
            style={{ borderColor: "#dee2e6" }}
          >
            <Row className="g-2 align-items-end flex-nowrap">
                       {/* ── YoY: Year selector + date range picker ── */}
              {mode === "yoy" && (
                <>
                  <Col xs={12} sm={3} md={2}>
                    <small className="text-muted d-block mb-1">Year</small>
                    <Select
                      options={YEAR_OPTIONS}
                      value={YEAR_OPTIONS.find((y) => y.value === period.year) ?? null}
                      onChange={(opt) => updatePeriod(period.id, { year: opt.value })}
                                      classNamePrefix="form-select"

                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </Col>
                  <Col xs={12} sm="auto" className="flex-grow-1">
                    <small className="text-muted d-block mb-1">Date range within year</small>
                    <RsDateRangePicker
                      placement="topStart"
                      value={pickerValue(period)}
                      onClean={() => clearDateRange(period.id, period)}
                      onOk={(dates) => handleDateRangeOk(dates, period.id)}
                      {...pickerBounds(period)}
                    />
                  </Col>
                </>
              )}

              {/* ── MoM: Month + Year selectors + date range picker ── */}
              {mode === "mom" && (
                <>
                  <Col xs={12} sm={4} md={3}>
                    <small className="text-muted d-block mb-1">Month</small>
                    <Select
                      options={MONTHS}
                      value={MONTHS.find((m) => m.value === period.month) ?? null}
                      onChange={(opt) => updatePeriod(period.id, { month: opt.value })}
                                      classNamePrefix="form-select"

                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </Col>
                  <Col xs={12} sm={3} md={2}>
                    <small className="text-muted d-block mb-1">Year</small>
                    <Select
                      options={YEAR_OPTIONS}
                      value={YEAR_OPTIONS.find((y) => y.value === period.year) ?? null}
                      onChange={(opt) => updatePeriod(period.id, { year: opt.value })}
                                      classNamePrefix="form-select"

                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </Col>
                  <Col xs={12} sm="auto" className="flex-grow-1">
                    <small className="text-muted d-block mb-1">Date range within month</small>
                    <RsDateRangePicker
                      placement="topStart"
                      value={pickerValue(period)}
                      onClean={() => clearDateRange(period.id, period)}
                      onOk={(dates) => handleDateRangeOk(dates, period.id)}
                      {...pickerBounds(period)}
                    />
                  </Col>
                </>
              )}

              {/* ── Custom: free date range picker only ── */}
              {mode === "custom" && (
                <Col xs={12} sm="auto" className="flex-grow-1">
                  <small className="text-muted d-block mb-1">Date range</small>
                  <RsDateRangePicker
                    placement="bottomStart"
                    value={pickerValue(period)}
                    onClean={() => clearDateRange(period.id, period)}
                    onOk={(dates) => handleDateRangeOk(dates, period.id)}
                  />
                </Col>
              )}

              {/* Remove button */}
              <Col xs="auto" className="d-flex align-items-end">
                <Button
                  variant="outline-danger"
                  size="sm"
                  style={{ height: 38, width: 38, flexShrink: 0 }}
                  onClick={() => removePeriod(period.id)}
                  title="Remove period"
                >
                  &times;
                </Button>
              </Col>
            </Row>
          </div>
        ))}
      </div>

      {/* Add period — no max */}
      <Button
        variant="primary"
        size="sm"
        className="mt-2"
        onClick={addPeriod}
      >
        + Add period
      </Button>

   
    </div>
  );
}